import { z } from 'zod';

// Treat an empty-string env var (e.g. `VITE_AUTH_HOST=` copied from .env.example,
// or an empty CI output) as unset, so zod's `.default()` and the `??` realm
// fallback below apply instead of yielding a malformed OIDC URL such as
// `https:///realms//protocol/...`.
const emptyAsUndefined = (v: unknown): unknown => (v === '' ? undefined : v);

const schema = z.object({
  MODE: z.enum(['development', 'staging', 'production', 'test']).default('development'),
  // Keycloak realm per environment — build arg from CI (env-context.auth_realm).
  // Unset in local dev; `env.authRealm` below falls back to `dev`.
  VITE_AUTH_REALM: z.preprocess(emptyAsUndefined, z.string().optional()),
  // Keycloak host for OIDC sign-in. Defaults to the legacy portal's default
  // (portal-cicd.yml `AUTH_HOST_OVERRIDE || 'auth.gooten.com'`); override in
  // .env.local to point local dev at a sandbox/lab Keycloak.
  VITE_AUTH_HOST: z.preprocess(emptyAsUndefined, z.string().default('auth.gooten.com')),
  // Portal's own public base URL, used as the OIDC `redirect_uri` origin (the
  // legacy `Config.Portal.Url` / `$PORTAL_FQDN`). CI sets it per-env to the
  // STABLE Keycloak-whitelisted host (`https://portal.<env>.ordermesh.io`), and
  // PR/preview builds inherit the development value — so the redirect_uri stays
  // valid even on ephemeral preview hostnames. Unset locally → `oidc.ts` falls
  // back to `window.location.origin`.
  VITE_PORTAL_URL: z.preprocess(emptyAsUndefined, z.string().optional()),
  VITE_GA_TRACKING_ID_PROD: z.string().optional(),
  VITE_GA_TRACKING_ID_STG: z.string().optional(),
  VITE_LAUNCHDARKLY_CLIENT_ID: z.string().optional(),
  VITE_SENTRY_DSN: z.string().optional(),
  // Master on/off switch for reCAPTCHA in the UI. Defaults to enabled; set to
  // the string `false` (CI sets it per env via `vars.captcha_enabled`, and the
  // test runner forces it off) to skip reCAPTCHA entirely — `executeRecaptcha`
  // then returns null so no token is sent. Any value other than `false` (or
  // unset) leaves it enabled.
  VITE_CAPTCHA_ENABLED: z.preprocess(
    (v) => (v === undefined || v === '' ? undefined : v !== 'false'),
    z.boolean().default(true),
  ),
  VITE_CAPTCHA_SITE_KEY: z.preprocess(emptyAsUndefined, z.string().optional()),
});

const parsed = schema.safeParse({
  MODE: import.meta.env.MODE,
  VITE_AUTH_REALM: import.meta.env.VITE_AUTH_REALM,
  VITE_AUTH_HOST: import.meta.env.VITE_AUTH_HOST,
  VITE_PORTAL_URL: import.meta.env.VITE_PORTAL_URL,
  VITE_GA_TRACKING_ID_PROD: import.meta.env.VITE_GA_TRACKING_ID_PROD,
  VITE_GA_TRACKING_ID_STG: import.meta.env.VITE_GA_TRACKING_ID_STG,
  VITE_LAUNCHDARKLY_CLIENT_ID: import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID,
  VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  VITE_CAPTCHA_ENABLED: import.meta.env.VITE_CAPTCHA_ENABLED,
  VITE_CAPTCHA_SITE_KEY: import.meta.env.VITE_CAPTCHA_SITE_KEY,
});

if (!parsed.success) {
  throw new Error(`Invalid environment configuration: ${parsed.error.message}`);
}

const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isProduction = hostname === 'portal.ordermesh.io';
const isStaging = hostname === 'portal.stg.ordermesh.io';

export const env = {
  ...parsed.data,
  isProduction,
  isStaging,
  isDevelopment: parsed.data.MODE === 'development',
  isTest: parsed.data.MODE === 'test',
  /**
   * Resolved Keycloak realm for OIDC sign-in. CI sets `VITE_AUTH_REALM` per
   * environment; when it's unset (local dev) fall back by deployed host and
   * default to `dev` — mirroring the legacy portal, whose local `overrides.json`
   * targeted the dev environment by default.
   */
  authRealm: parsed.data.VITE_AUTH_REALM ?? (isProduction ? 'prod' : isStaging ? 'stage' : 'dev'),
} as const;
